<template>
    <div class="sms-to-list">
        <b-form @submit.prevent>
            <b-row>
                <b-col cols="4" class="bg-soft-light border-2 border-end border-light pt-3">
                    <b-row class="mx-0 mb-3">
                        <b-col cols="12" class="mb-3 px-0 px-md-1" :key="utf8UpdateKey">
                            <b-form-group
                                id="settings-sms-coding"
                            >
                                <div class="form-check form-switch form-switch-md custom-checkbox mb-2">
                                    <label class="form-check-label" for="settings-sms-utf8">{{ $t('profile.utf8_sms_sending') }}</label>
                                    <input type="checkbox" id="settings-sms-utf8" name="settings-sms-utf8" class="form-check-input" v-model="model.is_sms_utf8" />
                                </div>
                            </b-form-group>
                        </b-col>
                        <b-col cols="12">
                            <b-form-group :label="$t('sms.type_of_sending')">
                                <b-form-radio-group
                                    id="sending-type"
                                    v-model="toList"
                                    :options="listOptions"
                                    name="sending-type-options"
                                ></b-form-radio-group>
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <b-row class="mx-0 mb-3">
                        <b-col cols="12">
                            <b-form-group
                                id="id-contact-list"
                                :label="$t('contact.contact_list')"
                                label-for="id-contact-list-input"
                                label-cols="12"
                                label-class="pt-0"
                                content-cols="12"
                                :state="listState"
                                :invalid-feedback="error.id_contact_list"
                                class="form-floating"
                            >
                                <b-form-select class="form-select form-select-sm" id="id-contact-list-input" v-model="model.id_contact_list" :options="contacts" value-field="m_id" text-field="formatted"></b-form-select>
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <b-row class="m-0">
                        <b-col cols="12" class="px-0 px-md-1">
                            <b-form-group
                                id="send-date"
                                class="w-100"
                                :label="$t('sms.send_date')"
                                label-for="send-date-input"
                                label-cols="12"
                                content-cols="12"
                                :state="sendDateState"
                                :invalid-feedback="error.send_date"
                            >
                                <b-form-input id="send-date-input" type="datetime-local" required v-model="model.send_date"></b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-col>
                <b-col cols="8">
                    <b-form-group
                        id="message"
                        :label="$t('sms.message')"
                        label-for="message-input"
                        label-cols="12"
                        content-cols="12"
                        :state="messageState"
                        :invalid-feedback="error.message"
                    >
                        <template #label>
                            <label class="col-12 col-form-label" for="message-input">
                                <span class="me-3">{{ $t('sms.message') }}</span>
                                <small class="text-muted" v-if="model.message">{{ $t('sms.message_length', { sms: sms, chars: chars }) }}</small>
                            </label>
                        </template>
                        <div class="position-relative rounded overflow-hidden">
                            <b-form-textarea ref="message" id="message-input" :rows="10" v-model="model.message" @change="updateMessage" @input="checkMessageContainsLink" :placeholder="$t('sms.placeholders.message')" @keydown="handleKeydown"></b-form-textarea>
                            <div class="bg-light position-absolute bottom-0 w-100 border p-1">
                                <b-button :disabled="disabledLink" size="sm" variant="primary" :title="$t('sms.add_link')" @click="openShortLink"><i class="fas fa-link"></i></b-button>
                                <b-button class="ms-2" size="sm" variant="primary" :title="$t('sms.add_token', {token: $t('tokens.user')})" @click="addToken('user')"><i class="fas fa-user"></i></b-button>
                                <b-button class="ms-2" size="sm" variant="primary" :title="$t('sms.add_token', {token: $t('tokens.phone')})" @click="addToken('phone')"><i class="fas fa-phone"></i></b-button>
                                <template v-for="(item, key) in additionalData">
                                    <b-button :key="key" class="ms-2" size="sm" variant="primary" :title="$t('sms.add_token', {token: item})" @click="addToken(item)">{{ item }}</b-button>
                                </template>
                            </div>
                        </div>
                    </b-form-group>
                </b-col>
            </b-row>
            <b-row class="mt-3">
                <b-col cols="12" class="text-end">
                    <b-button class="w-100 btn-rounded px-3" variant="primary" block size="sm" @click="send"><i class="fas fa-paper-plane me-2"></i>{{ $t('sms.send') }}</b-button>
                </b-col>
            </b-row>
            <b-modal ref="link-modal" centered body-bg-variant="light" v-model="showLinkModal" @hide="emptyLink">
                <template #modal-title>
                    {{ $t('sms.add_link') }}
                </template>
                <short-link-form v-model="link"></short-link-form>
                <template #modal-footer>
                    <b-button variant="primary" class="btn-rounded px-3" size="sm" @click="addShortLink">{{ $t('sms.insert') }}</b-button>
                    <b-button size="sm" class="btn-rounded px-3" @click="showLinkModal = false;">{{ $t('sms.cancel') }}</b-button>
                </template>
            </b-modal>
        </b-form>
    </div>
</template>

<script>
import {mapGetters} from "vuex";
import moment from "moment";
import Swal from "sweetalert2";
import ShortLinkForm from "@/components/sms/short-link-form.vue";
import ShortLinkMixin from "@/mixins/shortLink.js";
import UserDataMixin from "@/mixins/userData.js";

export default {
    name: "list",

    mixins: [ShortLinkMixin, UserDataMixin],

    components: {ShortLinkForm},

    emits: ['update:IsUtf8', 'updateToList', 'update:link', 'update:message'],

    props: {
        message: {
            type: String
        },

        isUtf8: {
            type: Boolean,
        },

        deflink: {}
    },

    data: function () {
        return {
            model: {
                message: null,
                send_date: null,
                is_sms_utf8: null,
                link: {}
            },
            error: {
                message: null,
                send_date: null
            },
            sortBy: (this.paginator && this.paginator.column) || 'id',
            sortDesc: (this.paginator && this.paginator.desc) || true,
            toList: true,
            listOptions: [
                { text: this.$t('sms.to_target'), value: false },
                { text: this.$t('sms.to_list'), value: true }
            ],
            utf8UpdateKey: 1
        }
    },

    computed: {
        ...mapGetters({
            contacts: 'contact/selectList',
            messages: 'sms/toList',
            loading: 'sms/loading',
            paginator: 'sms/toListPager',
            storedMessage: 'sms/message'
        }),

        additionalData: function () {
            if (! this.model.id_contact_list) {
                return [];
            }

            return this.contacts.find(c => c.m_id === this.model.id_contact_list)?.additional_data || [];
        },

        sendDateState: function () {
            return this.error.send_date === null;
        },

        sms: function () {
            let text = this.model.message;
            let isGsmCode = true;
            let smsL = 67;
            let smsO = 70;
            let textLength = this.chars;

            if (! textLength) {
                return 0;
            }

            if (! this.model.is_sms_utf8) {
                text = this.replaceHungarianGSM(text);
                textLength = text.length + this.linkLength;

                for (let i = 0; i < textLength; i++) {
                    let char = text.substr(i, 1);

                    if (! this.gsm7Characters.includes(char)) {
                        isGsmCode = false;
                        break;
                    }
                }

                if (isGsmCode) {
                    smsL = 153;
                    smsO = 160;
                }
            }

            return textLength <= smsO ? 1 : Math.ceil(textLength / smsL);
        },

        linkLength: function () {
            return this.model.link && this.model.link.url && this.model.link.url.length ? 23 : 0;
        },

        messageLength: function () {
            if (!this.chars) {
                return 0;
            }

            return this.model.message.length;
        },

        chars: function () {
            if (! this.model.message?.length) {
                return 0;
            }

            return this.model.message.length + this.linkLength;
        },

        messageState: function () {
            return this.error.message === null
        },

        listState: function () {
            return this.error.id_contact_list === null
        },

        fields: function () {
            return [
                {
                    key: "id",
                    label: '#',
                    thClass: 'gridjs-th',
                    sortable: true
                },
                {
                    key: "contact_list_name",
                    label: this.$t('contact.contact_list'),
                    thClass: 'gridjs-th',
                    sortable: true
                },
                {
                    key: "create_date",
                    label: this.$t('sms.create_date'),
                    thClass: 'gridjs-th border-left-0',
                    formatter: (value) => {
                        return moment(value).format('YYYY. MM. DD. HH:mm')
                    },
                    sortable: true
                },
                {
                    key: "deleted_date",
                    label: this.$t('sms.deleted_date'),
                    thClass: 'gridjs-th border-left-0',
                    formatter: (value) => {
                        return value ? moment(value).format('YYYY. MM. DD. HH:mm') : value
                    },
                    sortable: true
                },
                {
                    key: "is_deleted",
                    label: this.$t('sms.is_deleted'),
                    thClass: 'gridjs-th border-left-0',
                    tdClass: 'text-center',
                    sortable: true
                },
                {
                    key: "remaining_sms",
                    label: this.$t('sms.remaining_sms'),
                    thClass: 'gridjs-th border-left-0',
                    tdClass: 'text-end',
                    sortable: true
                },
                {
                    key: "sent_sms",
                    label: this.$t('sms.sent_sms'),
                    thClass: 'gridjs-th border-left-0',
                    tdClass: 'text-end',
                    sortable: true
                },
                {
                    key: "sms",
                    label: this.$t('sms.message'),
                    thClass: 'gridjs-th border-left-0',
                    sortable: true
                },
                {
                    key: "actions",
                    label: '',
                    thClass: 'gridjs-th'
                },
            ];
        },

        gsm7Characters: function () {
            const charCodes = [' ', '@', '£', '$', '¥', 'è', 'é', 'ù', 'ì', 'ò', 'Ç', 'Ø', 'ø', 'Å', 'å', 'Δ', '_', 'Φ', 'Γ', 'Λ', 'Ω', 'Π', 'Ψ', 'Σ', 'Θ', 'Ξ', '^', '{', '}', '\\', '[', '~', ']', '|', '€', 'Æ', 'æ', 'ß', 'É', '!', '"', '#', '¤', '%', '&', '(', ')', '*', '+', ',', '-', '.', '/', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', ':', ';', '<', '=', '>', '?', '¡', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'Ä', 'Ö', 'Ñ', 'Ü', '§', '¿', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z', 'ä', 'ö', 'ñ', 'ü', 'à'];
            [10, 12, 13, 27].forEach(code => {
                charCodes.push(String.fromCharCode(code));
            });
            return charCodes;
        }
    },

    methods: {
        send: function () {
            if (!this.model.id_contact_list) {
                this.error.id_contact_list = this.$t('validate.required', { item: this.$t('contact.contact_list')});
            }

            if (!this.model.message) {
                this.error.message = this.$t('validate.required', { item: this.$t('sms.message')});
            }

            if (this.model.send_date) {
                this.model.send_date = new Date(this.model.send_date).formattedDate();
            }

            this.$store.dispatch('sms/sendToList', this.model).then(response => {
                if (response?.error !== true) {
                    // this.model = {};
                }
            });
        },

        toPage: function (val) {
            this.page = val
            this.delayedFetch()
        },

        delayedFetch: function () {
            this.$store.dispatch('sms/fetchToListDelayed', {
                limit: this.perPage,
                offset: this.pager && this.pager.offset,
                page: this.page,
                sorting: {
                    column: this.sortBy,
                    desc: this.sortDesc
                }
            });
        },

        revoke: function (id) {
            Swal.fire({
                title: this.$t('table.confirm'),
                text: this.$t('sms.confirm_revoke'),
                icon: 'warning',
                showCancelButton: true,
                buttonsStyling: false,
                customClass: {
                    confirmButton: 'btn btn-warning btn-md me-1',
                    cancelButton: 'btn btn-primary btn-md ms-1',
                },
                confirmButtonText: this.$t('table.yes'),
                cancelButtonText: this.$t('table.no')
            }).then((result) => {
                if (result.isConfirmed) {
                    this.$store.dispatch('sms/revokeSentToList', id).then(() => {
                        Swal.fire(
                            this.$t('sms.message_revoked'),
                            this.$t('sms.selected_item_is_revoked'),
                            'success'
                        )
                    });
                }
            })
        },

        setSendDate: function () {
            if (! this.model.send_date) {
                const now = new Date();
                this.model.send_date = now.formattedDate();
            }
        },

        replaceHungarianGSM: function (text) {
            const map = {
                "á": "à",
                "í": "ì",
                "ó": "ò",
                "ú": "ù",
                "ő": "ö",
                "ű": "ü",
                "Á": "Å",
                "Í": "I",
                "Ú": "U",
                "Ó": "O",
                "Ő": "Ö",
                "Ű": "Ü"
            };

            return text.replace(/[áíóúőűÁÍÚÓŐŰ]/g, char => map[char]);
        }
    },

    watch: {
        toList: function (boolean) {
            this.$emit('updateToList', boolean);
        },
        sortBy: function () {
            this.delayedFetch();
        },
        sortDesc: function () {
            this.delayedFetch();
        },
        'model.is_sms_utf8': function (bool) {
            this.$emit('update:IsUtf8', bool);
        },
        isUtf8: {
            immediate: true,
            handler: function (value) {
                if (value === 0 || value === '0' || value === '' || value === null || value === false) {
                    this.model.is_sms_utf8 = false;
                } else {
                    this.model.is_sms_utf8 = true;
                }
                this.utf8UpdateKey += 1;
            }
        },
    },

    created() {
        this.$store.dispatch('contact/fetchSelectList');
        this.$store.dispatch('sms/fetchToList');
    },

    mounted() {
        this.setSendDate();
        if (this.storedMessage) {
            this.model.message = this.storedMessage;
        }
        if (this.deflink) {
            this.model.link = this.deflink;
        }
    }
}
</script>

<style lang="scss" scoped>
input::placeholder, textarea::placeholder {
    color: lightgrey;
}

::v-deep {
    .custom-control-inline {
        display: inline-flex;
        &:not(:first-of-type) {
            margin-left: 1rem;
        }
        .custom-control-label {
            margin-bottom: 0;
            margin-left: 0.25rem;
        }
    }
}

.form-floating {
    .form-select-sm {
        padding-top: 1rem;
    }
    ::v-deep {
        label {
            opacity: .65;
            transform: scale(.85) translateX(-0.15rem);
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            padding: 1rem 0.75rem;
            pointer-events: none;
            border: 1px solid transparent;
            transform-origin: 0 0;
            transition: opacity .1s ease-in-out,transform .1s ease-in-out;
        }
    }
}
</style>
